import React, { useEffect } from "react";
import { Layers, Cpu, Bot, Printer, CheckCircle2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BusinessContact from "../components/BusinessContact";
import { setPageSeo } from "../lib/seo";
import { BUSINESS } from "../lib/business";

const TEAM = [
  {
    title: "Product & Systems",
    desc: "Product architecture, roadmapping, and delivery orchestration across disciplines.",
    icon: Layers,
  },
  {
    title: "Software Engineering",
    desc: "Frontend, backend, APIs, and cloud-native SaaS systems built for production scale.",
    icon: Cpu,
  },
  {
    title: "AI Engineering",
    desc: "LLM workflows, intelligent automation, and applied AI integrated into core product paths.",
    icon: Bot,
  },
  {
    title: "Hardware & 3D",
    desc: "Embedded systems, IoT integration, and 3D enclosure development from prototype to field-ready.",
    icon: Printer,
  },
];

const CREDIBILITY_POINTS = [
  `Legal name: ${BUSINESS.legalName}`,
  "Founder: Ishu Kumar",
  "Based in Electronic City Phase-2, Bengaluru",
  "Hands-on across software, AI, and hardware delivery",
  "Structured execution model from scope to launch",
  "Post-launch ownership for stabilization and scale",
];

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
    setPageSeo({
      title: "About DevzCorner — Product Engineering Team",
      description:
        "DevzCorner is a Bengaluru product engineering studio delivering software, AI, hardware, and 3D fabrication as one organization.",
      path: "/about",
    });
  }, []);

  return (
    <main className="relative bg-ink-0 text-white min-h-screen flex flex-col">
      <Navbar />
      <section className="relative flex-grow pt-32 pb-24">
        <div className="absolute inset-0 tech-grid opacity-25 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-4xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
              / About Devzcorner
            </div>
            <h1 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02]">
              Built as one delivery organization,
              <br />
              <span className="text-zinc-500">not a fragmented vendor chain.</span>
            </h1>
            <p className="mt-6 text-zinc-400 text-base md:text-xl max-w-3xl leading-relaxed">
              Devzcorner was built to solve a specific problem: products that require software,
              AI, and hardware often fail at vendor boundaries. Delivery here is structured so
              those boundaries do not exist.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-5">
              <div className="glass-card rounded-3xl overflow-hidden border border-white/10">
                <div className="aspect-[4/5] relative">
                  <img
                    src="https://images.unsplash.com/photo-1764545973653-94c40d993495?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHx0ZWNoJTIwZm91bmRlciUyMHBvcnRyYWl0JTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc2NDkxODYxfDA&ixlib=rb-4.1.0&q=85"
                    alt="Ishu Kumar, Devzcorner founder"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-0/95 via-ink-0/20 to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5">
                    <div className="font-display text-xl">Ishu Kumar</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mt-1">
                      Founder · Product & Systems Lead
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 glass-card rounded-2xl border border-white/10 p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
                  / Credibility
                </div>
                <ul className="space-y-2.5">
                  {CREDIBILITY_POINTS.map((point) => (
                    <li key={point} className="flex items-start gap-2.5 text-sm text-zinc-400 leading-relaxed">
                      <CheckCircle2 size={14} className="mt-0.5 text-accent-cyan shrink-0" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <BusinessContact className="mt-4" />
            </div>

            <div className="lg:col-span-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
                / Why this studio exists
              </div>
              <h2 className="font-display text-3xl md:text-5xl leading-[1.05] tracking-tight">
                Origin in a repeated failure pattern:
                <br />
                <span className="text-zinc-500">handoffs broke the product.</span>
              </h2>
              <p className="mt-5 text-zinc-400 max-w-2xl leading-relaxed">
                Early projects showed the same issue repeatedly: interface teams, backend teams,
                AI teams, and hardware vendors each delivered in isolation, while product quality
                degraded at the seams. Devzcorner was structured to operate as one system across
                disciplines so execution quality is preserved through launch.
              </p>

              <div className="mt-8">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
                  / Team capability
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {TEAM.map((item) => {
                    const Icon = item.icon;
                    return (
                      <article
                        key={item.title}
                        className="glass-card rounded-2xl border border-white/10 p-5"
                      >
                        <div className="w-10 h-10 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center mb-4 text-accent-cyan">
                          <Icon size={16} />
                        </div>
                        <h3 className="font-display text-xl tracking-tight">{item.title}</h3>
                        <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{item.desc}</p>
                      </article>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
